import Icon from "components/Icon";
import React, { memo, useEffect, useState } from "react";
import { useContext } from "react";
import { Spinner } from "react-bootstrap";
import { CService } from "services/class.base.service";
import { formatCurrency } from "utils/currency";
import { dateDBtoBR } from "utils/date";
import { createCancelToken } from "utils/http-common";
import { IExpandRow, NewTableContext } from ".";
import { DATA_TYPE_NEW_TABLE, IColumnNewTable } from "./interfaces";

interface ITableBodyProps{
  data: any[];
  columns: IColumnNewTable[];
  load?: boolean;
  children?: JSX.Element | JSX.Element[] | any;
  expandRow?: IExpandRow;
}

function getValue(data: any, c: IColumnNewTable, ignoreMask: boolean = false){
  let dataList = c.dataField.split("|");
  let dataPath = c.dataField.split(".");
  
  if(dataList.length>1){
    let values:any[] = [];
    dataList.forEach(item=>{
      let v = getValue(data,{...c, dataField:item},true);
      values.push(v)
    })
    
    if(c.mask){
      return c.mask(values);
    }else{
      return values.join(" | ");
    }
  } else if(dataPath.length>1){
    let dataField = "";
    let path = "";
    dataPath.forEach((item,k)=>{
      if(k>0){
        if(path.length>0)path += ".";
        path += item;
      } else {
        dataField = item;
      }
    })
    let value:any = data[dataField]?getValue(data[dataField],{...c, dataField:path}, ignoreMask):null;
    return value;
  } else {
    if(c.mask && !ignoreMask){
      if(data && data[c.dataField])
        return c.mask(data[c.dataField]);
      return c.mask(null);
    } else {
      if(data && data[c.dataField])
        return data[c.dataField];
      return null;
    }
  }
}

function getTypeValye(value: any, type: DATA_TYPE_NEW_TABLE | any){
  switch (type) {
    case DATA_TYPE_NEW_TABLE.DATE:
      return dateDBtoBR(value);
    case DATA_TYPE_NEW_TABLE.CURRENCY:
      if(!value) return formatCurrency(0)
      return formatCurrency(value);
    default:
      if(!value) return "---"
      return value;
  }
}

export default function TableBody({data, columns, load, children, expandRow}: ITableBodyProps){
  const [ openRowsList, setOpenRowsList ] = useState<number[]>([]);

  if(load){
    return (
      <tbody className="text-center">
        {children}
        <tr className="new-table-tb-tr">
          <td colSpan={columns.length+(expandRow?1:0)}><Spinner animation="border" /></td>
        </tr>
      </tbody>
    );
  }
  
  if(data.length === 0){
    return (
      <tbody className="text-center">
        {children}
        <tr className="new-table-tb-tr">
          <td colSpan={columns.length+(expandRow?1:0)}>Sem dados para mostrar.</td>
        </tr>
      </tbody>
    );
  }

  function isExpanded(row: number){
    return openRowsList.indexOf(row) !== -1;
  }

  function renderExpandRow(d:any, key_d:number){
    if(expandRow){
      if(expandRow.type){
        if(expandRow.type === "COL"){
          return <tr style={expandRow.style} className={"newtable-expand-row"+(isExpanded(key_d)?" newtable-expand-row-open":"")}>
            <td></td>
            {expandRow.render(d,key_d)}
          </tr>
        }
        if(expandRow.type === "ROW" && expandRow.getColumnInfo){
          let v = expandRow.render(d,key_d);
          if(Array.isArray(v)){
            return v.map((d,key)=>{
              return (
                <tr style={expandRow.style} key={key} className={"newtable-expand-row"+(isExpanded(key_d)?" newtable-expand-row-open":"")}>
                <td></td>
                {
                  columns.map((c, key_c)=>{
                    let style = {...c.styleBody};
                    if(c.isAction){
                      style = {...style, width: '1%', whiteSpace: 'nowrap'}
                    }
                    if(c.bodyNowrap) style.whiteSpace = "nowrap";
                    return (
                      <td key={key_c} style={{verticalAlign:"middle" ,...style}}>{
                        getTypeValye(getValue(d,c),c.type)
                      }</td>
                    );
                  })
                }
                </tr>
              )
            });
          }
          return <></>;
        }
        if(expandRow.type === "ROW"){
          let v = expandRow.render(d,key_d);
          if(Array.isArray(v)){
            return v.map((item,key)=>{
              return <tr style={expandRow.style} key={key} className={"newtable-expand-row"+(isExpanded(key_d)?" newtable-expand-row-open":"")}>
                <td></td>
                {item}
              </tr>
            })
          }
          return <></>;
        }
      }
      return (
        <tr style={expandRow.style} className={"newtable-expand-row"+(isExpanded(key_d)?" newtable-expand-row-open":"")}>
          <td colSpan={columns.length+1} className="p-0">
            {expandRow.render(d,key_d)}
          </td>
        </tr>
      );
    }
  }
  
  return (
    <tbody className="text-center">
      {children}
      {data.map((d, key_d)=>{
        return (
          <React.Fragment key={key_d}>
            <tr className="new-table-tb-tr" style={!!expandRow?{cursor: 'pointer'}:{}} onClick={()=>{
              if(!!expandRow){
                setOpenRowsList((old:number[])=>{
                  if(old.indexOf(key_d) !== -1){
                    return [...old.filter(n=>(n!==key_d))];
                  } else {
                    return [...old, key_d];
                  }
                })
              }
            }}>
              {!!expandRow?(
                <td className={"newtable-fit-cell newtable-icon-expand-row"+(isExpanded(key_d)?" newtable-expand-row-open":"")}>
                  <Icon name="caret-right"/>
                </td>
              ):<></>}
              {columns.map((c, key_c)=>{
                let style = {...c.styleBody};
                if(c.isAction){
                  style = {...style, width: '1%', whiteSpace: 'nowrap'}
                }
                if(c.bodyNowrap) style.whiteSpace = "nowrap";
                return (
                  <td key={key_c} style={{verticalAlign:"middle" ,...style}}>{
                    getTypeValye(getValue(d,c),c.type)
                  }</td>
                );
              })}
            </tr>
            {renderExpandRow(d,key_d)}
          </React.Fragment>
        );
      })}
    </tbody>
  );
}

interface ITableBodyDataProps{
  request: CService;
  filterDefaut?: any;
}
function __TableBodyData({request, filterDefaut}:ITableBodyDataProps){
  const { setData, filters, setLoad, setRequest, setFilter } = useContext(NewTableContext);

  useEffect(()=>{
    if(setRequest)setRequest(request);
  },[setRequest, request])

  useEffect(()=>{
    if(setFilter && filterDefaut)setFilter(filterDefaut);
  },[setFilter, filterDefaut]);

  useEffect(()=>{
    const {token, cancel} = createCancelToken();
    if(setLoad)setLoad(true);
    async function con(){
      try {
        let aux_filter = {...filters};
        if(filterDefaut){
          aux_filter = {...filterDefaut, ...aux_filter};
        }
        let _request = await request.getPaginated({params:aux_filter,cancelToken:token});
        if(setData)setData(_request.data, _request.pagination);
      } catch (error:any) {
        if(!error.cancel){
          if(setData)setData([],{});
        }
      }
    }
    
    let id = setTimeout(() =>{
      con();
    },500);
    return () => {
      clearTimeout(id);
      cancel();
    };
  }, [filters, setData, request, setLoad, filterDefaut]);

  return <></>;
}

export const TableBodyData = memo(__TableBodyData);